import type { IAtributo, IAtributoOpcao } from "interfaces/loja/atributosProduto";
import type {
  IVariacaoAtributoPayload,
  IVariacaoProdutoState,
} from "interfaces/loja/variacaoProduto";

const TIPOS_PARA_COMBINACAO: IAtributo["tipo"][] = ["LISTA", "COR"];

/**
 * Gera slug seguro para SKU (remove acentos e caracteres especiais).
 */
function slugParaSku(valor: string): string {
  return valor
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase()
    .slice(0, 20) || "X";
}

/** Abrevia texto para SKU no mesmo formato do backend: remove acentos, só alfanum, max len */
function abrevSKU(text: string, maxLen: number): string {
  if (!text?.trim()) return "U";
  const s = text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\W/g, "")
    .toUpperCase()
    .slice(0, maxLen);
  return s || "U";
}

function random6Digits(): string {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

/**
 * Gera SKU para uma variação no mesmo formato do backend:
 * [Marca][Tipo][Tamanho][Cor]-XXXXXX (ex: NKCAMPRE-485254).
 * Usado ao adicionar variação manualmente e no padrão.
 */
export function gerarSKUVariacaoManual(
  produtoNome: string,
  marcaNome: string,
  atributos: IVariacaoAtributoPayload[],
  atributosDisponiveis: IAtributo[]
): string {
  const marca = abrevSKU(marcaNome || "GEN", 3);
  const tipo = abrevSKU(produtoNome || "PROD", 4);
  let tamanho = "U";
  let cor = "U";
  if (atributos?.length && atributosDisponiveis?.length) {
    for (const a of atributos) {
      const atr = atributosDisponiveis.find((x) => x.id === a.atributoId);
      const nomeAtrib = (atr?.nome ?? "").toLowerCase();
      let valor = a.valorCor ?? "";
      if (a.opcaoId != null && atr?.opcoes) {
        const op = atr.opcoes.find((o) => o.id === a.opcaoId);
        valor = op?.valor ?? op?.label ?? "";
      }
      const v = abrevSKU(String(valor), 3);
      if (nomeAtrib.includes("tamanho") || nomeAtrib.includes("size")) tamanho = v;
      else if (nomeAtrib.includes("cor") || nomeAtrib.includes("color")) cor = v;
    }
  }
  const base = `${marca}${tipo}${tamanho}${cor}`.replace(/-/g, "");
  return `${base}-${random6Digits()}`;
}

/**
 * Produto cartesiano: gera todas as combinações de um array de arrays.
 * Ex: [[1,2], [3,4]] => [[1,3],[1,4],[2,3],[2,4]]
 */
function produtoCartesiano<T>(arrays: T[][]): T[][] {
  if (arrays.length === 0) return [[]];
  const [primeiro, ...resto] = arrays;
  const restanteCombinacoes = produtoCartesiano(resto);
  const resultado: T[][] = [];
  for (const item of primeiro) {
    for (const comb of restanteCombinacoes) {
      resultado.push([item, ...comb]);
    }
  }
  return resultado;
}

/**
 * Gera todas as variações a partir dos atributos selecionados (LISTA e COR).
 * SKU = sigla do produto + "-" + valores das opções (slug seguro).
 * preco e precoOriginal são aplicados a todas as variações geradas (ex.: da aba Dados).
 */
export function gerarCombinacoesVariacoes(
  atributos: IAtributo[],
  siglaProduto: string,
  opcoes?: { preco?: number; precoOriginal?: number; precoCusto?: number | null }
): IVariacaoProdutoState[] {
  const atributosComOpcoes = atributos.filter(
    (a) =>
      TIPOS_PARA_COMBINACAO.includes(a.tipo) &&
      Array.isArray(a.opcoes) &&
      a.opcoes.length > 0
  );

  if (atributosComOpcoes.length === 0) {
    return [];
  }

  type OpcaoEscolhida = {
    atributo: IAtributo;
    opcao: IAtributoOpcao;
  };

  const listasDeOpcoes: OpcaoEscolhida[][] = atributosComOpcoes.map((atr) =>
    (atr.opcoes || []).map((opcao) => ({ atributo: atr, opcao }))
  );

  const combinacoes = produtoCartesiano(listasDeOpcoes);
  const sigla = slugParaSku(siglaProduto || "PROD");

  return combinacoes.map((combinacao) => {
    const atributosPayload: IVariacaoAtributoPayload[] = combinacao.map(
      ({ atributo, opcao }) => {
        if (atributo.tipo === "LISTA") {
          return { atributoId: atributo.id, opcaoId: opcao.id };
        }
        return { atributoId: atributo.id, valorCor: opcao.valor };
      }
    );
    const partesSku = [sigla, ...combinacao.map((c) => slugParaSku(c.opcao.valor))];
    const sku = partesSku.join("-");
    return {
      sku,
      codigoBarras: "",
      preco: opcoes?.preco ?? 0,
      precoOriginal: opcoes?.precoOriginal ?? 0,
      precoCusto: opcoes?.precoCusto ?? null,
      estoque: 0,
      emEstoque: true,
      atributos: atributosPayload,
    };
  });
}
